import { useEffect, useState } from 'react';
import { Layout } from 'antd';
import {
  Activity,
  ArrowRight,
  Check,
  Container,
  Factory,
  MapPin,
  MoreHorizontal,
  Pencil,
  Radio,
  Trash2,
  Wind,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Card } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Switch } from 'components/ui/switch';
import { Skeleton } from 'components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from 'components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from 'components/ui/dropdown-menu';
import AddCard from './components/AddCard';
import SideNav from '../../Layout/SideNav';
import HeaderBar from '../../Layout/HeaderBar';
import FooterBar from '../../Layout/FooterBar';
import { SensorType } from '../../general/enums';
import { FeatureIds } from '../../modules/featureControl/featuresConfig';
import BlurOverlayContainer from '../../components/Overlay/BlurOverlayContainer';
import { isAdmin, isAks2talOnly } from 'components/AdminOnly';
import { useAppSelector } from 'hooks';
import { firestore, functions } from '../../firebase';
import { sensorTypeLabel } from '../SensorOverview/model';

const icons: Partial<Record<SensorType, LucideIcon>> = {
  [SensorType.MACHINE_ACTIVITY]: Activity,
  [SensorType.GPS_GY_NEO]: MapPin,
  [SensorType.GPS_SIMTRACKING]: MapPin,
  [SensorType.DUST_OLD]: Wind,
  [SensorType.FOOBOT]: Wind,
  [SensorType.ESP_AQ]: Wind,
  [SensorType.ESP_AQ_NEW]: Wind,
  [SensorType.GSM_PLANT]: Factory,
  [SensorType.GSM_CUSTOM]: Factory,
  [SensorType.LEVEL_SENSOR_INTERNAL]: Container,
  [SensorType.PLANT_MODULE]: Container,
};

const routeMap = {
  [SensorType.MACHINE_ACTIVITY]: '/machine-activity',
  [SensorType.GPS_GY_NEO]: null,
  [SensorType.GPS_SIMTRACKING]: '/gps-simtracking',
  [SensorType.DUST_OLD]: null,
  [SensorType.FOOBOT]: '/aq',
  [SensorType.ESP_AQ]: '/air-quality',
  [SensorType.ESP_AQ_NEW]: '/air-quality',
  [SensorType.GSM_PLANT]: '/gsm-plant',
  [SensorType.GSM_CUSTOM]: '/gsm-custom',
  [SensorType.LEVEL_SENSOR_INTERNAL]: '/level-sensor',
  [SensorType.PLANT_MODULE]: '/plant-module',
};

interface SensorData {
  id: string;
  deviceId?: string;
  title: string;
  createTime?: { toDate: () => Date } | null;
  type: SensorType;
  identifier?: string;
  status?: 'active' | 'inactive';
}

function Sensor({
  sensor,
  orgId,
  onDelete,
}: {
  sensor: SensorData;
  orgId: string;
  onDelete: () => void;
}) {
  const { id, deviceId, identifier, title, createTime, type, status } = sensor;
  const userId = useAppSelector((state) => state.user.details.uid);
  const canManage = isAdmin(userId);
  const canEditTitle = isAks2talOnly(userId);
  const [updating, setUpdating] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title || '');
  const [savingTitle, setSavingTitle] = useState(false);
  const [error, setError] = useState('');
  const Icon = icons[type] || Radio;
  const date = createTime?.toDate?.();
  const dateString =
    date && Number.isFinite(date.getTime()) ? format(date, 'dd/MM/yyyy · HH:mm') : 'Ukendt';
  const href = `/orgs/${orgId}/sensors/${id}${routeMap[type] || ''}`;
  const statusLabel = status === 'active' ? 'Aktiv' : status === 'inactive' ? 'Inaktiv' : 'Ukendt';

  useEffect(() => {
    if (!isEditingTitle) setDraftTitle(title || '');
  }, [isEditingTitle, title]);

  const handleStatusChange = async (checked: boolean) => {
    if (updating) return;
    setUpdating(true);
    setError('');
    try {
      const batch = firestore.batch();
      batch.update(firestore.collection('sensors').doc(id), {
        status: checked ? 'active' : 'inactive',
      });
      batch.update(firestore.collection('orgs').doc(orgId).collection('sensors').doc(id), {
        status: checked ? 'active' : 'inactive',
      });
      await batch.commit();
      toast.success(checked ? 'Sensoren er aktiveret' : 'Sensoren er deaktiveret');
    } catch {
      setError('Sensorens status kunne ikke ændres. Prøv igen.');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveTitle = async () => {
    const nextTitle = draftTitle.trim();
    if (savingTitle || !nextTitle) return;
    if (nextTitle === (title || '').trim()) {
      setIsEditingTitle(false);
      return;
    }
    setSavingTitle(true);
    setError('');
    try {
      await functions.httpsCallable('settings')({
        requestType: 'UPDATE_SENSOR_TITLE',
        orgId,
        sensorId: id,
        title: nextTitle,
      });
      setIsEditingTitle(false);
      toast.success('Sensorens navn er gemt');
    } catch {
      setError('Sensorens navn kunne ikke gemmes. Prøv igen.');
    } finally {
      setSavingTitle(false);
    }
  };

  return (
    <li className="px-4 py-5 sm:px-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-6">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <div
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
              type === SensorType.MACHINE_ACTIVITY
                ? 'bg-amber-50 text-amber-700'
                : 'bg-slate-100 text-slate-600'
            }`}
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            {isEditingTitle ? (
              <form
                className="flex items-center gap-1"
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleSaveTitle();
                }}
              >
                <Input
                  autoFocus
                  aria-label="Sensorens navn"
                  value={draftTitle}
                  maxLength={120}
                  required
                  disabled={savingTitle}
                  onChange={(event) => setDraftTitle(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape' && !savingTitle) setIsEditingTitle(false);
                  }}
                />
                <Button
                  type="submit"
                  size="icon"
                  variant="ghost"
                  aria-label="Gem navn"
                  loading={savingTitle}
                  disabled={savingTitle || !draftTitle.trim()}
                >
                  <Check />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label="Annuller navneændring"
                  disabled={savingTitle}
                  onClick={() => setIsEditingTitle(false)}
                >
                  <X />
                </Button>
              </form>
            ) : (
              <Link
                className="block break-words text-base font-semibold text-foreground hover:underline focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                to={href}
              >
                {title || 'Sensor uden navn'}
              </Link>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              <span>{sensorTypeLabel(type)}</span>
              {canEditTitle && (deviceId || identifier) && (
                <span className="break-all">
                  · {type === SensorType.MACHINE_ACTIVITY ? 'Boks-ID' : 'ID'}:{' '}
                  {deviceId || identifier}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-8 gap-y-4 pl-[3.75rem] lg:pl-0">
          <div className="min-w-[7rem]">
            <p className="mb-1.5 text-xs text-muted-foreground">Registrering</p>
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className={
                  status === 'active'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                    : 'text-muted-foreground'
                }
              >
                {statusLabel}
              </Badge>
              {canManage && (
                <Switch
                  aria-label={`Aktivér ${title || 'sensor'}`}
                  checked={status === 'active'}
                  disabled={updating}
                  onCheckedChange={handleStatusChange}
                />
              )}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-xs text-muted-foreground">Oprettet</p>
            <span className="whitespace-nowrap text-sm text-muted-foreground">{dateString}</span>
          </div>
          <div className="flex items-center gap-1 lg:min-w-[9rem] lg:justify-end">
            <Button asChild variant="outline" size="sm">
              <Link to={href} aria-label={`Vis data for ${title || 'sensor'}`}>
                Vis data <ArrowRight aria-hidden="true" />
              </Link>
            </Button>
            {(canEditTitle || canManage) && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Handlinger for ${title || 'sensor'}`}
                  >
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onCloseAutoFocus={(event) => {
                    if (isEditingTitle) event.preventDefault();
                  }}
                >
                  {canEditTitle && (
                    <DropdownMenuItem
                      onSelect={() => {
                        setError('');
                        setIsEditingTitle(true);
                      }}
                    >
                      <Pencil />
                      Rediger navn
                    </DropdownMenuItem>
                  )}
                  {canEditTitle && canManage && <DropdownMenuSeparator />}
                  {canManage && (
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={onDelete}
                    >
                      <Trash2 />
                      Slet sensor
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
      {error && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      )}
    </li>
  );
}

interface Props {
  sensors: SensorData[];
  loading: boolean;
  orgId: string;
}

export default function SensorList({ sensors, loading, orgId }: Props) {
  const [deletingSensor, setDeletingSensor] = useState<SensorData | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const handleDelete = async () => {
    if (!deletingSensor || deleting) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const id = deletingSensor.id;
      if (!orgId || !id) throw new Error('Invalid sensor identity');
      const batch = firestore.batch();
      batch.delete(firestore.collection('sensors').doc(id));
      batch.delete(firestore.collection('orgs').doc(orgId).collection('sensors').doc(id));
      await batch.commit();
      setDeletingSensor(null);
      toast.success('Sensoren er slettet');
    } catch {
      setDeleteError('Sensoren kunne ikke slettes. Prøv igen.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <SideNav />
      <Layout>
        <HeaderBar />
        <BlurOverlayContainer featureKey={FeatureIds.SENSORS}>
          <main className="mx-auto w-full max-w-7xl p-4 sm:p-6 lg:p-8">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="m-0 text-2xl font-semibold tracking-tight">Sensorer</h1>
                  {!loading && <Badge variant="secondary">{sensors.length}</Badge>}
                </div>
                <p className="mb-0 mt-1 text-sm text-muted-foreground">
                  Se målinger og administrér kundens sensorer.
                </p>
              </div>
              <AddCard />
            </div>
            <Card className="overflow-hidden shadow-sm">
              {loading ? (
                <div role="status" className="space-y-5 p-6">
                  <span className="sr-only">Henter sensorer…</span>
                  {[0, 1, 2].map((key) => (
                    <Skeleton key={key} className="h-14 w-full" />
                  ))}
                </div>
              ) : sensors.length ? (
                <ul aria-label="Kundens sensorer" className="m-0 list-none divide-y p-0">
                  {sensors.map((sensor) => (
                    <Sensor
                      key={sensor.id}
                      sensor={sensor}
                      orgId={orgId}
                      onDelete={() => {
                        setDeleteError('');
                        setDeletingSensor(sensor);
                      }}
                    />
                  ))}
                </ul>
              ) : (
                <div className="px-6 py-12 text-center">
                  <Radio className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                  <h2 className="text-base font-semibold">Ingen sensorer endnu</h2>
                  <p className="mb-0 text-sm text-muted-foreground">
                    Kundens sensorer vises her, når de er tilføjet.
                  </p>
                </div>
              )}
            </Card>
          </main>
        </BlurOverlayContainer>
        <FooterBar />
      </Layout>
      <Dialog
        open={!!deletingSensor}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeletingSensor(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Slet sensor?</DialogTitle>
            <DialogDescription>
              Vil du slette registreringen af {deletingSensor?.title || 'sensoren'} hos kunden?
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <p role="alert" className="text-sm text-destructive">
              {deleteError}
            </p>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={deleting} onClick={() => setDeletingSensor(null)}>
              Annuller
            </Button>
            <Button
              variant="destructive"
              loading={deleting}
              disabled={deleting}
              onClick={handleDelete}
            >
              Slet sensor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
